import React, { useState, useRef, useEffect } from "react";
import { postWithAuthCall } from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";

// Floating, global chat assistant. Answers aggregate questions about the app's
// data (members, subscriptions, reports, campaigns) from a live backend snapshot.
const SUGGESTIONS = [
  "How many members do we have, and how many are verified?",
  "What is our subscription revenue / MRR?",
  "How many new members joined in the last 7 days?",
  "How many open reports are there right now?",
];

const GRADIENT = "linear-gradient(90deg, #055D9D 0%, #7A2E8E 55%, #E40288 100%)";

const S = {
  launcher: {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 58,
    height: 58,
    borderRadius: "50%",
    background: GRADIENT,
    color: "#fff",
    fontSize: 26,
    border: "none",
    boxShadow: "0 8px 24px -8px rgba(20,24,60,0.45)",
    cursor: "pointer",
    zIndex: 9999,
    lineHeight: 1,
  },
  panel: {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 370,
    maxWidth: "92vw",
    height: 540,
    maxHeight: "80vh",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 24px 60px -20px rgba(20,24,60,0.5)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 9999,
    border: "1px solid #e9ebf2",
  },
  header: {
    background: GRADIENT,
    color: "#fff",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  close: {
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 24,
    cursor: "pointer",
    lineHeight: 1,
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: 14,
    background: "#f7f8fb",
  },
  sugg: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "#fff",
    border: "1px solid #e6e8f1",
    borderRadius: 10,
    padding: "9px 11px",
    marginBottom: 8,
    fontSize: 12.5,
    color: "#043152",
    cursor: "pointer",
  },
  userBubble: {
    background: "#055D9D",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: "14px 14px 2px 14px",
    maxWidth: "82%",
    fontSize: 13.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  aiBubble: {
    background: "#fff",
    color: "#1f2733",
    padding: "8px 12px",
    borderRadius: "14px 14px 14px 2px",
    maxWidth: "82%",
    fontSize: 13.5,
    border: "1px solid #eef0f4",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: 10,
    borderTop: "1px solid #eef0f4",
    background: "#fff",
  },
  input: {
    flex: 1,
    border: "1px solid #d9dbe6",
    borderRadius: 10,
    padding: "9px 11px",
    fontSize: 13.5,
    outline: "none",
  },
  send: {
    background: "#E40288",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "9px 16px",
    fontSize: 13.5,
    cursor: "pointer",
  },
  footer: {
    fontSize: 10,
    color: "#9aa1b4",
    textAlign: "center",
    padding: "4px 0 8px",
  },
};

const AdminAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = (text) => {
    const question = (text !== undefined ? text : input).trim();
    if (!question || loading) return;

    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const next = [...messages, { role: "user", content: question }];
    setMessages(next);
    setInput("");
    setLoading(true);

    postWithAuthCall(
      ApiConfig.ASSISTANT_CHAT,
      JSON.stringify({ question, history })
    )
      .then((res) => {
        setLoading(false);
        const answer = res && !res.error ? res.data && res.data.answer : null;
        setMessages([
          ...next,
          {
            role: "assistant",
            content:
              answer ||
              (res && res.message) ||
              "Sorry, something went wrong. Please try again.",
          },
        ]);
      })
      .catch(() => {
        setLoading(false);
        setMessages([
          ...next,
          {
            role: "assistant",
            content: "Sorry, the assistant is unavailable right now.",
          },
        ]);
      });
  };

  if (!open) {
    return (
      <button
        style={S.launcher}
        onClick={() => setOpen(true)}
        title="Ask the admin assistant"
        aria-label="Open admin assistant"
      >
        💬
      </button>
    );
  }

  return (
    <div style={S.panel}>
      <div style={S.header}>
        <div>
          <div style={{ fontWeight: 700 }}>Admin Assistant</div>
          <div style={{ fontSize: 11, opacity: 0.85 }}>Ask about your data</div>
        </div>
        <button style={S.close} onClick={() => setOpen(false)} aria-label="Close">
          ×
        </button>
      </div>

      <div style={S.list} ref={listRef}>
        {messages.length === 0 && (
          <div>
            <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
              Ask about members, subscriptions, reports, campaigns and more.
            </div>
            {SUGGESTIONS.map((s, i) => (
              <button key={i} style={S.sugg} onClick={() => send(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
              marginBottom: 8,
            }}
          >
            <div style={m.role === "user" ? S.userBubble : S.aiBubble}>
              {m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ ...S.aiBubble, color: "#6b7280" }}>Thinking…</div>
          </div>
        )}
      </div>

      <div style={S.inputRow}>
        <input
          style={S.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") send();
          }}
          placeholder="Ask a question…"
          disabled={loading}
        />
        <button style={S.send} onClick={() => send()} disabled={loading}>
          Send
        </button>
      </div>
      <div style={S.footer}>Answers are based on aggregate data only.</div>
    </div>
  );
};

export default AdminAssistant;
